(ns frontend.view.nev.kyc.process.screening-profiles.model
  "State of the screening profiles that the KYC process page gets through the
   scope of its KYC profile. Each KYC profile has its own state path, so two
   pages cannot overwrite each other.

   The scope sends an entity again when a transaction changes it, also when the
   entity leaves the scope. A profile that a transaction disables or unlinks
   from the KYC profile therefore arrives as an upsert, and this model drops it."
  (:require
   [common.schema.entities.screening-profile.materialization :as sp-materialization]
   [common.util :as cu]))

(def root-path
  [:kyc-process-screening-profiles])

(def load-status-key
  :kyc-screening-profiles/load-status)

(def profiles-by-id-key
  :kyc-screening-profiles/profiles-by-id)

(def error-key
  :kyc-screening-profiles/error)

(defn state-path
  [kyc-profile-id]
  (conj root-path kyc-profile-id))

(defn ref-id
  "The id of a reference. A reference is an id or a map with :db/id."
  [value]
  (or (:db/id value)
      value))

(defn screening-profile?
  [entity]
  (= :entity.type/screening-profile (:entity/type entity)))

(defn linked-profile?
  "True when `entity` is an enabled screening profile that points at the KYC
   profile with :screening-profile/kyc-profiles. The scope also brings other
   screening profiles, for example the person profiles of a company profile."
  [{:keys [kyc-profile-id entity]}]
  (boolean
   (and (screening-profile? entity)
        (not (:common/disabled? entity))
        (->> (:screening-profile/kyc-profiles entity)
             (map ref-id)
             (some #{kyc-profile-id})))))

(defn entities-received
  "The state after a message of the scope. A message ends the loading, also when
   it holds no screening profile. A screening profile that is no longer linked
   or is disabled leaves the state. Entities of other types are ignored.

   A snapshot (`:snapshot?`) replaces the profiles of the state. The server sends
   one when the stream opens and after each reconnect, and it holds every entity
   that the scope has then. A profile that left the scope while the connection
   was down is not in it, so merging would keep that profile. `:removed-ids` are
   the ids that left the scope. They leave the state, also when the same message
   holds a linked copy of the profile."
  [state {:keys [kyc-profile-id entities removed-ids snapshot?]}]
  (let [screening-profiles (filter screening-profile? entities)
        linked? (fn [entity]
                  (linked-profile? {:kyc-profile-id kyc-profile-id
                                    :entity         entity}))
        unlinked-ids (->> screening-profiles
                          (remove linked?)
                          (map :db/id))
        earlier-profiles (if snapshot?
                           {}
                           (get state profiles-by-id-key {}))
        linked-pairs (->> screening-profiles
                          (filter linked?)
                          (map (fn [entity]
                                 [(:db/id entity) entity])))
        merged-profiles (into (apply dissoc earlier-profiles unlinked-ids) linked-pairs)
        profiles-by-id (apply dissoc merged-profiles removed-ids)]
    (-> state
        (assoc load-status-key :loaded)
        (assoc profiles-by-id-key profiles-by-id)
        (dissoc error-key))))

(defn failed
  "The state after the scope failed. A later message of the scope ends the failure."
  [state error]
  (-> state
      (assoc load-status-key :failed)
      (assoc error-key error)))

(defn load-status
  "One of :loading, :loaded and :failed."
  [state]
  (get state load-status-key :loading))

(defn profile-list
  "The screening profiles of the state in the order of their ids."
  [state]
  (->> (get state profiles-by-id-key)
       (vals)
       (sort-by :db/id)
       (vec)))

(def profile-ids-path
  [:kyc-process-profile-ids])

(def profile-id-status-key
  :kyc-process-profile/status)

(def profile-id-key
  :kyc-process-profile/profile-id)

(def profile-id-error-key
  :kyc-process-profile/error)

(def kyc-processes-attributes
  "The attribute of each type of KYC profile that points at its KYC processes.
   A KYC process has no pointer to its KYC profile, and the backend does not
   store :common/containing-entity on it."
  [:kyc-profile/kyc-processes
   :tenant-kyc-profile/kyc-processes
   :contract-kyc-profile/kyc-processes])

(defn profile-id-path
  [kyc-process-id]
  (conj profile-ids-path kyc-process-id))

(defn holds-process?
  "True when `entity` points at the KYC process with one of the
   kyc-processes attributes. The pointers are ids or maps with :db/id."
  [{:keys [entity kyc-process-id]}]
  (boolean
   (some (fn [attribute]
           (->> (get entity attribute)
                (map ref-id)
                (some #{kyc-process-id})))
         kyc-processes-attributes)))

(defn profile-id-of-process
  "The id of the KYC profile that holds the KYC process, or nil. `entities` are
   the entities that the backend sent with the process. They include the
   profile, because the profile points at the process. Do not pass the session."
  [{:keys [kyc-process-id entities]}]
  (->> entities
       (filter (fn [entity]
                 (holds-process? {:entity         entity
                                  :kyc-process-id kyc-process-id})))
       (map :db/id)
       (sort)
       (first)))

(defn profile-id-resolved
  "The state of the profile id after the backend sent the entities of the
   process. The id is nil when no entity holds the process."
  [{:keys [kyc-process-id entities]}]
  {profile-id-status-key :resolved
   profile-id-key        (profile-id-of-process {:kyc-process-id kyc-process-id
                                                 :entities       entities})})

(defn profile-id-failed
  "The state of the profile id after the load of the process failed."
  [error]
  {profile-id-status-key :failed
   profile-id-error-key  error})

(defn profile-id-status
  "One of :resolving, :resolved and :failed."
  [state]
  (get state profile-id-status-key :resolving))

(defn profile-id
  [state]
  (get state profile-id-key))

(defn process-load-status
  "One of :loading, :loaded and :failed for the screening profiles of the KYC
   process. It is :loading until the profile id is known and the scope of the
   profile has sent its first message. It is :failed when the process load
   failed, when no profile holds the process, or when the scope failed."
  [db kyc-process-id]
  (let [state (get-in db (profile-id-path kyc-process-id))]
    (case (profile-id-status state)
      :resolving :loading
      :failed :failed
      (if-let [kyc-profile-id (profile-id state)]
        (load-status (get-in db (state-path kyc-profile-id)))
        :failed))))

(defn process-profile-list
  "The screening profiles of the KYC profile that holds the KYC process, or an
   empty vector while the profile id is not known."
  [db kyc-process-id]
  (if-let [kyc-profile-id (profile-id (get-in db (profile-id-path kyc-process-id)))]
    (profile-list (get-in db (state-path kyc-profile-id)))
    []))

(defn cleared
  "`db` without the profile id of the KYC process and without the state of the
   screening profiles of that KYC profile."
  [db kyc-process-id]
  (let [kyc-profile-id (profile-id (get-in db (profile-id-path kyc-process-id)))]
    (cond-> db
      (get-in db (profile-id-path kyc-process-id)) (update-in profile-ids-path dissoc kyc-process-id)
      kyc-profile-id (update-in root-path dissoc kyc-profile-id))))

(defn prefer-active
  "The first active profile of `screening-profiles`, or else the first profile,
   or nil."
  [screening-profiles]
  (or (->> screening-profiles
           (cu/filter-by :screening-profile/screening-status :screening-status/active)
           (first))
      (first screening-profiles)))

(defn find-person-profile
  "The profile of `person` among `screening-profiles`. The match key is the one of
   the backend sync. The profile type is not checked, as in the KYC table
   before."
  [screening-profiles person]
  (let [deduplication-id (sp-materialization/person->deduplication-id person)]
    (->> screening-profiles
         (filter (fn [screening-profile]
                   (= deduplication-id
                      (sp-materialization/person-profile->deduplication-id screening-profile))))
         (prefer-active))))

(defn find-legal-entity-profile
  "The company profile of `legal-entity` among `screening-profiles`. The match key
   is the one of the backend sync, so the business ID is part of it."
  [screening-profiles legal-entity]
  (let [deduplication-id (sp-materialization/legal-entity->deduplication-id legal-entity)]
    (->> screening-profiles
         (cu/filter-by :screening-profile/type :screening-profile-type/company)
         (filter (fn [screening-profile]
                   (= deduplication-id
                      (sp-materialization/company-profile->deduplication-id screening-profile))))
         (prefer-active))))
